import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import {from, Observable, of, throwError} from 'rxjs';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {RedirectHandlerService} from '../../services/test/redirect-handler.service';
import {Router} from '@angular/router';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {AuthenticationResult, InteractionStatus} from '@azure/msal-browser';
import {TenantContextService} from '../../../services/tenant-context.service';

@Injectable()
export class MsalCustomerInterceptor implements HttpInterceptor {
  private customerToken?: AuthenticationResult;
  private employeeTokens = new Map<string, AuthenticationResult>();
  private redirecting = false;
  private interactionStatus: InteractionStatus = InteractionStatus.Startup;

  constructor(
    private readonly employeeMsalService: MsalService,
    private readonly customerMsalService: RedirectHandlerService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly tenantContext: TenantContextService
  ) {
    this.msalBroadcastService.inProgress$.subscribe(status => {
      this.interactionStatus = status;
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.startsWith('https://graph.microsoft.com')) {
      return next.handle(request);
    }
    let token$: Observable<AuthenticationResult>;
    const url = this.router.url;
  
    if (url.includes('/auth/form/') && this.customerMsalService.activeAccount) {
      token$ = this.getValidCustomerToken(() =>
        this.customerMsalService.acquireTokenSilent()
      );
    } else {
      const accounts = this.employeeMsalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        // Only kick off a redirect if MSAL is not already mid-interaction.
        // Firing loginRedirect() while another PCA (e.g. the M365 Graph instance)
        // is settling its own redirect throws BrowserAuthError: interaction_in_progress.
        if (!this.redirecting && this.interactionStatus === InteractionStatus.None) {
          this.redirecting = true;
          console.warn('No active employee account - redirecting to login.');
          this.employeeMsalService.loginRedirect();
        }
        return throwError(() => new Error('No active account - redirecting to login'));
      }
      const account = accounts[0];
      const scopes =
        request.method === 'GET'
          ? environment.apiConfig.IdentityAPI.scopes.read
          : environment.apiConfig.IdentityAPI.scopes.write;
  
      token$ = this.getValidEmployeeToken(scopes, () =>
        this.employeeMsalService.instance.acquireTokenSilent({
          account,
          scopes: [...scopes],
        })
      );
    }
  
    return token$.pipe(
      switchMap((result: AuthenticationResult) => {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${result.accessToken}`
        };
        // Tell the backend module which tenant database to use. Only our module APIs need it.
        const tenantId = this.tenantContext.tenantId;
        if (tenantId && this.isModuleApi(request.url)) {
          headers['X-BC-Tenant'] = tenantId;
        }
        const cloned = request.clone({ setHeaders: headers });
        return next.handle(cloned);
      }),
      catchError(err => {
        console.error('Token acquisition failed:', err);
        if (err.status === 401 && !this.redirecting && this.interactionStatus === InteractionStatus.None) {
          this.redirecting = true;
          this.employeeMsalService.loginRedirect();
        }
        return throwError(() => err);
      })
    );
  }

  // --- CUSTOMER (single token type) ---
  private getValidCustomerToken(
    acquire: () => Promise<AuthenticationResult>
  ): Observable<AuthenticationResult> {
    if (this.customerToken && !this.isExpired(this.customerToken.expiresOn)) {
      return of(this.customerToken);
    }
    return from(acquire()).pipe(
      tap(result => (this.customerToken = result))
    );
  }

  // --- EMPLOYEE (different scopes) ---
  private getValidEmployeeToken(
    scopes: string[],
    acquire: () => Promise<AuthenticationResult>
  ): Observable<AuthenticationResult> {
    const scopeKey = scopes.sort().join(' '); // normalize scope key
    const cached = this.employeeTokens.get(scopeKey);

    if (cached && !this.isExpired(cached.expiresOn)) {
      return of(cached);
    }

    return from(acquire()).pipe(
      tap(result => this.employeeTokens.set(scopeKey, result))
    );
  }

  private isExpired(expiresOn?: Date | null): boolean {
    if (!expiresOn) return true;
    return expiresOn.getTime() - Date.now() < 2 * 60 * 1000;
  }

  // Attach the tenant header to EVERY call to one of our backend module APIs so they all read the
  // tenant's databases. Never to Graph, the Billing/licensing API, or third parties (a custom
  // header would also trigger a CORS preflight those hosts may reject).
  private isModuleApi(url: string): boolean {
    const cfg = environment.apiConfig as any;
    const billing = cfg.billingAPI?.endpoint as string | undefined;
    if (url.startsWith('https://graph.microsoft.com')) return false;
    if (billing && url.startsWith(billing)) return false;

    // 1) Exact configured endpoints (covers localhost and any env).
    const endpoints = [
      cfg.IdentityAPI?.endpoint,
      cfg.corporateAPI?.endpoint,
      cfg.legacyInvoicingApi?.endpoint,
      (environment as any).invoicing_api,
    ].filter(Boolean) as string[];
    if (endpoints.some(e => url.startsWith(e))) return true;

    // 2) Robust fallback: any of our module API hosts (dev or prod suffixes), so no call is missed
    //    if it uses a slightly different/hardcoded base than the configured endpoint.
    return /:\/\/(bc-id-api|bc-corp-api|bc-inv-api|bc-inv-legacy-api)[a-z0-9.-]*\.azurewebsites\.net/i.test(url);
  }
}
